import type {
  CustomerAgencySerializer,
  CustomerBrandSerializer,
  CustomerChurnAgencySerializer,
  CustomerChurnBrandSerializer,
  CustomerChurnSummarySerializer,
  CustomerSuccessNoteSerializer,
  CustomerSuccessSummarySerializer,
  ManualFieldsSerializer,
  WindowMetricsSerializer,
} from "@/generated/DjangoTypes";
import {useGenericInfiniteQueryWithCount, useGenericQuery} from "@/hooks/useQueryHooks";
import {client} from "@/httpClient";
import {queryClient} from "@/queryClient";
import {useMutation} from "@tanstack/react-query";

const BASE = "/customer_success/agencies/";

export type TCpmBand = "crit" | "good" | "warn" | null;

export type TCustomerSuccessStatus = NonNullable<ManualFieldsSerializer["status"]>;

export type TMetricsWindow = "lifetime" | "period";

/**
 * The generated interfaces type every `SerializerMethodField` as `null` (the backend methods carry no
 * return annotation), so the fields below are restated with the types the API actually returns.
 */
export type TCustomerSuccessNote = Omit<CustomerSuccessNoteSerializer, "author_name" | "created" | "id"> & {
  author_name: string | null;
  created: string;
  id: number;
};

export type TCustomerAgency = Omit<
  CustomerAgencySerializer,
  | "agency_picture"
  | "cancellation_reason"
  | "currency"
  | "current_period_price"
  | "happiness_score"
  | "is_trialing"
  | "latest_note"
  | "lifetime"
  | "lifetime_total_cpm_band"
  | "period"
  | "period_total_cpm_band"
  | "renewal_price"
  | "renewed_after_month"
  | "status"
  | "tenure_started_at"
> & {
  agency_picture: string | null;
  cancellation_reason: string | null;
  currency: string | null;
  current_period_price: number;
  happiness_score: number | null;
  is_trialing: boolean;
  latest_note: TCustomerSuccessNote | null;
  lifetime: WindowMetricsSerializer;
  lifetime_total_cpm_band: TCpmBand;
  period: WindowMetricsSerializer;
  period_total_cpm_band: TCpmBand;
  renewal_price: number | null;
  renewed_after_month: boolean | null;
  status: TCustomerSuccessStatus;
  tenure_started_at: string | null;
};

export type TCustomerBrand = Omit<
  CustomerBrandSerializer,
  "brand_picture" | "lifetime" | "period" | "tiktok_search"
> & {
  brand_picture: string | null;
  lifetime: WindowMetricsSerializer;
  period: WindowMetricsSerializer;
  tiktok_search: boolean;
};

export const CUSTOMER_SUCCESS_KEYS = {
  mNote: (agencyId: number) => [`${BASE}${agencyId}/notes/`],
  qAgency: (agencyId: number) => [`${BASE}${agencyId}/`],
  qAgencies: (params: string) => [`${BASE}?${params}`],
  qBrands: (agencyId: number) => [`${BASE}${agencyId}/brands/`],
  qChurnBrands: (agencyId: number) => [`${BASE}${agencyId}/churn_brands/`],
  qChurnSummary: (params: string) => [`${BASE}summary/?${params}`],
  qNotes: (agencyId: number) => [`${BASE}${agencyId}/notes/`],
  qSummary: [`${BASE}summary/`],
} as const;

const invalidateAll = () =>
  queryClient.invalidateQueries({predicate: query => String(query.queryKey[0]).startsWith(BASE)});

export const useAddCustomerSuccessNoteMutation = (agencyId: number) =>
  useMutation({
    mutationFn: (text: string) => client.post(CUSTOMER_SUCCESS_KEYS.mNote(agencyId)[0], {text}),
    mutationKey: CUSTOMER_SUCCESS_KEYS.mNote(agencyId),
    onSuccess: invalidateAll,
  });

export const useCustomerBrandsQuery = (agencyId: number) =>
  useGenericInfiniteQueryWithCount<TCustomerBrand>({
    queryKey: CUSTOMER_SUCCESS_KEYS.qBrands(agencyId),
    refetchOnMount: query => query.state.status === "error",
  });

export const useCustomerChurnBrandsQuery = (agencyId: number) =>
  useGenericInfiniteQueryWithCount<CustomerChurnBrandSerializer>({
    queryKey: CUSTOMER_SUCCESS_KEYS.qChurnBrands(agencyId),
    refetchOnMount: query => query.state.status === "error",
  });

export const useCustomerAgencyQuery = (agencyId: number) =>
  useGenericQuery<TCustomerAgency>({queryKey: CUSTOMER_SUCCESS_KEYS.qAgency(agencyId)});

export const useCustomerAgenciesQuery = (params: string) =>
  useGenericInfiniteQueryWithCount<TCustomerAgency>({queryKey: CUSTOMER_SUCCESS_KEYS.qAgencies(params)});

export const useCustomerSuccessNotesQuery = (agencyId: number) =>
  useGenericQuery<TCustomerSuccessNote[]>({queryKey: CUSTOMER_SUCCESS_KEYS.qNotes(agencyId)});

export const useCustomerSuccessSummaryQuery = () =>
  useGenericQuery<CustomerSuccessSummarySerializer>({queryKey: CUSTOMER_SUCCESS_KEYS.qSummary});

export const useCustomerChurnAgenciesQuery = (params: string) =>
  useGenericInfiniteQueryWithCount<CustomerChurnAgencySerializer>({queryKey: CUSTOMER_SUCCESS_KEYS.qAgencies(params)});

export const useCustomerChurnSummaryQuery = (params: string) =>
  useGenericQuery<CustomerChurnSummarySerializer>({queryKey: CUSTOMER_SUCCESS_KEYS.qChurnSummary(params)});
